import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from './theme/theme';
import { useAuthStore } from './stores/authStore';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { LandingPage } from './components/landing/LandingPage';
import PatientDashboard from './components/dashboard/PatientDashboard';
import { EmployeeDashboard } from './components/dashboard/EmployeeDashboard';
import { FamilyDashboard } from './components/dashboard/FamilyDashboard';
import { PatientSearch } from './components/dashboard/PatientSearch';
import VideoCall from './components/video/VideoCall';
import CalendarView from './components/notifications/CalendarView';
import NotificationSettings from './components/notifications/NotificationSettings';
import AccessGroupsAdmin from './components/admin/AccessGroupsAdmin';
import UserManagement from './components/admin/UserManagement';
import AdminControlRoom from './components/admin/AdminControlRoom';
import SecurityLogs from './components/admin/SecurityLogs';
import AuditLog from './components/admin/AuditLog';
import SystemConfig from './components/admin/SystemConfig';
import Announcements from './components/admin/Announcements';
import Reports from './components/admin/Reports';
import AIAgentLogs from './components/admin/AIAgentLogs';
import ActivateAccount from './components/auth/ActivateAccount';
import ResetPassword from './components/auth/ResetPassword';
import { registerPushNotifications } from './utils/pushNotifications';
import PWAInstallBanner from './components/common/PWAInstallBanner';
import './index.css';

const TOKEN_CHECK_INTERVAL = 60 * 1000; // 60 seconds
const TOKEN_REFRESH_THRESHOLD = 5 * 60; // refresh when < 5 min left

// Componente para rutas protegidas
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, checkAuth } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole) {
    // Admins can access employee routes too
    const hasAccess = user?.role === requiredRole || 
      (requiredRole === 'employee' && user?.role === 'admin');
    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

// Componente para rutas públicas (redirige si ya está autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, checkAuth } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Componente para redirigir al dashboard correcto según rol
const DashboardRedirect = () => {
  const { user } = useAuthStore();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'patient':
      return <Navigate to="/dashboard/patient" replace />;
    case 'employee':
    case 'admin':
      return <Navigate to="/dashboard/employee" replace />;
    case 'family':
      return <Navigate to="/dashboard/family" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Componente de página no autorizada
const UnauthorizedPage = () => (
  <div className="unauthorized-page">
    <h1>Acceso No Autorizado</h1>
    <p>No tienes permisos para acceder a esta página.</p>
    <button onClick={() => window.location.href = '/dashboard'}>
      Volver al Dashboard
    </button>
  </div>
);

function App() {
  const { checkAuth, token, isAuthenticated, refreshToken } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const shouldRefresh = useCallback(() => {
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const secondsLeft = payload.exp - Date.now() / 1000;
      return secondsLeft > 0 && secondsLeft < TOKEN_REFRESH_THRESHOLD;
    } catch {
      return false;
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (shouldRefresh()) {
        refreshToken();
      }
    }, TOKEN_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, shouldRefresh, refreshToken]);

  // Register push notifications when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      registerPushNotifications();
    }
  }, [isAuthenticated]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          {isAuthenticated && <PWAInstallBanner />}
          <Routes>
          {/* Rutas públicas */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Redirección general al dashboard */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRedirect />
            </ProtectedRoute>
          } />

          {/* Dashboards específicos por rol */}
          <Route path="/dashboard/patient" element={
            <ProtectedRoute requiredRole="patient">
              <PatientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employee" element={
            <ProtectedRoute requiredRole="employee">
              <EmployeeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employee/patients" element={
            <ProtectedRoute requiredRole="employee">
              <PatientSearch />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/family" element={
            <ProtectedRoute requiredRole="family">
              <FamilyDashboard />
            </ProtectedRoute>
          } />

          {/* Ruta para videollamadas */}
          <Route path="/video-call/:callId" element={
            <ProtectedRoute>
              <VideoCall />
            </ProtectedRoute>
          } />

          {/* Calendario y Notificaciones */}
          <Route path="/calendar" element={
            <ProtectedRoute>
              <CalendarView />
            </ProtectedRoute>
          } />
          <Route path="/notifications/settings" element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          } />

          {/* Admin: Gestión de grupos de acceso */}
          <Route path="/admin/groups" element={
            <ProtectedRoute requiredRole="employee">
              <AccessGroupsAdmin />
            </ProtectedRoute>
          } />

          {/* Admin: Gestión de usuarios */}
          <Route path="/admin/users" element={
            <ProtectedRoute requiredRole="admin">
              <UserManagement />
            </ProtectedRoute>
          } />

          {/* Admin: Control Room */}
          <Route path="/admin/control-room" element={
            <ProtectedRoute requiredRole="admin">
              <AdminControlRoom />
            </ProtectedRoute>
          } />

          {/* Admin: Security Logs */}
          <Route path="/admin/security" element={
            <ProtectedRoute requiredRole="admin">
              <SecurityLogs />
            </ProtectedRoute>
          } />

          {/* Admin: Audit Log */}
          <Route path="/admin/audit" element={
            <ProtectedRoute requiredRole="admin">
              <AuditLog />
            </ProtectedRoute>
          } />

          {/* Admin: System Config */}
          <Route path="/admin/config" element={
            <ProtectedRoute requiredRole="admin">
              <SystemConfig />
            </ProtectedRoute>
          } />

          {/* Admin: Announcements */}
          <Route path="/admin/announcements" element={
            <ProtectedRoute requiredRole="admin">
              <Announcements />
            </ProtectedRoute>
          } />

          {/* Admin: Reports */}
          <Route path="/admin/reports" element={
            <ProtectedRoute requiredRole="admin">
              <Reports />
            </ProtectedRoute>
          } />

          {/* Admin: AI Agent Logs */}
          <Route path="/admin/ai-logs" element={
            <ProtectedRoute requiredRole="admin">
              <AIAgentLogs />
            </ProtectedRoute>
          } />

          {/* Página de no autorizado */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Ruta para cualquier otra URL no encontrada */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;